import { makeAutoObservable, runInAction } from 'mobx';

class PostsStore {
  posts = [];
  followingPosts = [];
  loading = false;
  error = null;
  rootStore = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Set loading state
  setLoading(loading) {
    this.loading = loading;
  }

  // Set error state
  setError(error) {
    this.error = error;
  }

  // Fetch user's posts
  async fetchPosts() {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.rootStore.authenticatedApiCall('api/posts');
      
      runInAction(() => {
        this.posts = response;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch following posts
  async fetchFollowingPosts() {
    this.setLoading(true);
    this.setError(null);

    try {
      const posts = await this.rootStore.authenticatedApiCall('api/posts/following');
      
      runInAction(() => {
        this.followingPosts = posts;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Create a new post
  async createPost(postData) {
    this.setLoading(true);
    this.setError(null);

    try {
      const newPost = await this.rootStore.authenticatedApiCall(
        'api/posts',
        {},
        'POST',
        postData
      );
      
      runInAction(() => {
        this.posts.unshift(newPost);
        this.loading = false;
      });

      return { success: true, post: newPost };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Update a post
  async updatePost(postId, postData) {
    this.setLoading(true);
    this.setError(null);

    try {
      const updatedPost = await this.rootStore.authenticatedApiCall(
        `api/posts/${postId}`,
        {},
        'PUT',
        postData
      );

      runInAction(() => {
        const index = this.posts.findIndex(post => post.id === postId);
        if (index !== -1) {
          this.posts[index] = updatedPost;
        }
        this.loading = false;
      });

      return { success: true, post: updatedPost };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Delete a post
  async deletePost(postId) {
    this.setLoading(true);
    this.setError(null);

    try {
      await this.rootStore.authenticatedApiCall(
        `api/posts/${postId}`,
        {},
        'DELETE'
      );

      runInAction(() => {
        this.posts = this.posts.filter(post => post.id !== postId);
        this.loading = false;
      });

      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Like a post
  async likePost(postId) {
    this.setError(null);

    try {
      await this.rootStore.authenticatedApiCall(
        `api/likes`,
        {},
        'POST',
        {
          target_type: 'post',
          target_id: postId
        }
      );

      runInAction(() => {
        const post = this.posts.find(p => p.id === postId);
        if (post) {
          post.userLiked = true;
          post.likesCount = (post.likesCount || 0) + 1;
        }
      });

      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
      });
      return { success: false, error: this.error };
    }
  }

  // Unlike a post
  async unlikePost(postId) {
    this.setError(null);

    try {
      await this.rootStore.authenticatedApiCall(
        `api/likes`,
        {},
        'DELETE',
        {
          target_type: 'post',
          target_id: postId
        }
      );

      runInAction(() => {
        const post = this.posts.find(p => p.id === postId);
        if (post) {
          post.userLiked = false;
          post.likesCount = Math.max(0, (post.likesCount || 1) - 1);
        }
      });

      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
      });
      return { success: false, error: this.error };
    }
  }

  // Get recent posts
  getRecentPosts(limit = 10) {
    return this.posts.slice(0, limit);
  }

  // Clear error
  clearError() {
    this.error = null;
  }

  // Reset store
  reset() {
    this.posts = [];
    this.loading = false;
    this.error = null;
  }
}

export { PostsStore }; 